import type { ModuleSplitSpec } from "./moduleSplitContract";

export interface ModuleMeasurement {
    height: number;
    spec?: ModuleSplitSpec;
}

const measurements = new Map<string, ModuleMeasurement>();

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

function fnv1a32(input: string): string {
    let hash = FNV_OFFSET_BASIS;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, FNV_PRIME);
    }
    return (hash >>> 0).toString(36);
}

function contentFingerprint(mod: any): string {
    try {
        const json = JSON.stringify(mod);
        return `${json.length.toString(36)}-${fnv1a32(json)}`;
    } catch {
        return "0";
    }
}

export function keyFor(mod: any, containerWidth: number): string {
    return `${mod.id}::${containerWidth}::${contentFingerprint(mod)}`;
}

export function getMeasurement(key: string): ModuleMeasurement | undefined {
    return measurements.get(key);
}

export function setMeasurement(key: string, value: ModuleMeasurement): void {
    measurements.set(key, value);
}

export function buildIdToHeight(
    modules: any[],
    containerWidth: number,
): Map<string, number> {
    const result = new Map<string, number>();
    for (const m of modules) {
        if (!m?.id) continue;
        const measured = measurements.get(keyFor(m, containerWidth));
        if (measured !== undefined) result.set(m.id, measured.height);
    }
    return result;
}

export function buildIdToSpec(
    modules: any[],
    containerWidth: number,
): Map<string, ModuleSplitSpec> {
    const result = new Map<string, ModuleSplitSpec>();
    for (const m of modules) {
        if (!m?.id) continue;
        const measured = measurements.get(keyFor(m, containerWidth));
        if (measured?.spec) result.set(m.id, measured.spec);
    }
    return result;
}

export function uncachedModules(
    modules: any[],
    containerWidth: number,
): any[] {
    return modules.filter(
        (m) => m?.id && !measurements.has(keyFor(m, containerWidth)),
    );
}

export function clearCache(): void {
    measurements.clear();
}
